import React, { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { HiOutlineMail } from 'react-icons/hi';
import { TbBell, TbPlus } from 'react-icons/tb';
import { MdOutlineMarkunreadMailbox } from 'react-icons/md';
import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import { OptionsButtonOption } from '../../../types/misc';
import { communicationTypeIcons } from '../../../utils/iconUtils';
import { CommunicationType } from '../../../types/communication';
import OptionsButton from '../../components/buttons/OptionsButton';
import useActiveSubscription from '../../../hooks/community/useActiveSubscription';
import PremiumRequiredCard from '../../components/cards/PremiumRequiredCard';
import ModalLayout from '../../components/layouts/ModalLayout';
import { hasRequiredSubscriptionForCommunication } from '../../../utils/subscriptionUtils';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { CommunityFeature } from '../../../types/community';
import CommunicationsTable from '../../components/tables/CommunicationsTable';
import communicationsService from '../../../services/community/communicationsService';
import useCommunications from '../../../hooks/community/useCommunications';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
// eslint-disable-next-line import/extensions
import PostsTab from './PostsTab';

export default function CommunicationsPage() {
  const { t } = useTranslation();
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const navigate = useNavigate();
  const activeSubscription = useActiveSubscription();
  const community = useSelectedCommunity();
  const communications = useCommunications();
  const { emails, notifications, postcards } = communications ?? {};

  const wallEnabled = community?.features?.[CommunityFeature.WALL] === true;

  useEffect(() => {
    communicationsService.getEmails();
    communicationsService.getNotifications();
    communicationsService.getPostcards();
  }, []);

  const hasCommunicationsData =
    communications !== null && (emails?.length || notifications?.length || postcards?.length);

  const CommunicationPageTab = {
    COMMUNICATIONS: t('page.communicationOverview.tabs.communications'),
    POSTS: t('page.communicationOverview.tabs.posts'),
  } as const;

  type CommunicationTab = (typeof CommunicationPageTab)[keyof typeof CommunicationPageTab];

  const tabs: CommunicationTab[] = wallEnabled
    ? [CommunicationPageTab.COMMUNICATIONS, CommunicationPageTab.POSTS]
    : [CommunicationPageTab.COMMUNICATIONS];

  const [activeTab, setActiveTab] = useState<CommunicationTab>(CommunicationPageTab.COMMUNICATIONS);

  const startCreate = (type: CommunicationType) => {
    if (!hasRequiredSubscriptionForCommunication(type, activeSubscription)) {
      setPremiumModalOpen(true);
      return;
    }
    navigate('/communication/create', { state: { type } });
  };

  const options: OptionsButtonOption[] = Object.values(CommunicationType).map((type) => ({
    icon: communicationTypeIcons[type],
    name: t(`page.communicationOverview.${type.toLocaleLowerCase()}`),
    onClick: () => startCreate(type),
  }));

  const communicationsContent = hasCommunicationsData ? (
    <CommunicationsTable />
  ) : (
    <EmptyScreenView />
  );

  const renderTabContent = (tab: CommunicationTab): JSX.Element => {
    if (tab === CommunicationPageTab.POSTS) {
      return <PostsTab createModalOpen={createPostOpen} setCreateModalOpen={setCreatePostOpen} />;
    }
    return communicationsContent;
  };

  const primaryButton =
    activeTab === CommunicationPageTab.POSTS ? (
      <PrimaryButton
        text={t('page.communicationOverview.posts.create')}
        icon={FaPlus}
        onClick={() => setCreatePostOpen(true)}
      />
    ) : (
      <OptionsButton options={options}>
        <Button variant="primary">
          <FaPlus className="h-4 w-4" />
          {t('page.communicationOverview.create')}
        </Button>
      </OptionsButton>
    );

  const quickActions: {
    type: CommunicationType;
    icon: IconType;
    title: string;
    description: string;
  }[] = [
    {
      type: CommunicationType.EMAIL,
      icon: HiOutlineMail,
      title: t('page.communicationOverview.email'),
      description: t('page.communicationOverview.quickActions.email'),
    },
    {
      type: CommunicationType.NOTIFICATION,
      icon: TbBell,
      title: t('page.communicationOverview.notification'),
      description: t('page.communicationOverview.quickActions.notification'),
    },
    {
      type: CommunicationType.POSTCARD,
      icon: MdOutlineMarkunreadMailbox,
      title: t('page.communicationOverview.postcard'),
      description: t('page.communicationOverview.quickActions.postcard'),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('page.communicationOverview.title')}
        subtitle={t('page.communicationOverview.subtitle')}
        primaryButton={primaryButton}
      />

      {activeTab === CommunicationPageTab.COMMUNICATIONS && (
        <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
          {quickActions.map(({ type, icon: Icon, title, description }) => (
            <button
              key={type}
              type="button"
              onClick={() => startCreate(type)}
              className="flex items-center gap-3 rounded-[12px] border border-primary-100 bg-primary-100/40 px-4 py-3 text-left transition-colors hover:bg-primary-100">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[8px] bg-surface text-primary-900">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-primary-900">{title}</p>
                <p className="truncate text-xs text-gray-500">{description}</p>
              </div>
              <TbPlus className="h-5 w-5 flex-shrink-0 text-primary-300" />
            </button>
          ))}
        </div>
      )}

      <PageWithTabsLayout
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        renderTabContent={renderTabContent}
      />
      <ModalLayout closeButton open={premiumModalOpen} setOpen={setPremiumModalOpen}>
        <PremiumRequiredCard />
      </ModalLayout>
    </>
  );
}
